<script setup lang="ts">
import { computed, nextTick, onUnmounted, ref, watch } from 'vue';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/vue';
import type { CountryCode } from 'libphonenumber-js/max';
import { $t } from '@/composables/translations/useTranslate';
import {
  DEFAULT_PHONE_COUNTRY,
  PHONE_COUNTRY_BY_CODE,
  PHONE_COUNTRY_OPTIONS,
  type PhoneCountryOption,
} from '@/constants/checkout/phoneCountries';
import { formatNational, joinPhone, parsePhoneInput, splitPhone } from '@/utils/formatting/phone';

/**
 * Phone input with a calling-code dropdown (VAM-294).
 *
 * v-model is the full international number (`+49 160 1234567`); the component
 * splits it into country + national part. Typing or pasting a number starting with
 * `+` / `00` switches the country automatically, and the national trunk `0` is
 * dropped. DE/AT/CH are listed first with a flag, then a separator, then all other
 * countries A–Z (Headless UI type-ahead jumps to a country by typing its name).
 */

interface Props {
  id?: string;
  name: string;
  label?: string;
  placeholder?: string;
  modelValue?: string | null;
  disabled?: boolean;
  error?: boolean;
  errorMessage?: string;
}

const props = withDefaults(defineProps<Props>(), {
  id: undefined,
  label: undefined,
  placeholder: undefined,
  modelValue: null,
  disabled: false,
  error: false,
  errorMessage: undefined,
});

const emit = defineEmits<{
  'update:modelValue': [value: string];
  blur: [event: FocusEvent];
}>();

const inputId = computed(
  () => props.id ?? `phone-${props.name}-${Math.random().toString(36).substring(2, 9)}`
);
const errorId = computed(() => `${inputId.value}-error`);

const country = ref<CountryCode>(DEFAULT_PHONE_COUNTRY);
const national = ref('');
const inputRef = ref<HTMLInputElement | null>(null);
let lastEmitted: string | null = null;

const selectedCountry = computed<PhoneCountryOption>(
  () => PHONE_COUNTRY_BY_CODE.get(country.value) ?? PHONE_COUNTRY_OPTIONS[0]
);
const pinnedOptions = PHONE_COUNTRY_OPTIONS.filter((option) => option.pinned);
const otherOptions = PHONE_COUNTRY_OPTIONS.filter((option) => !option.pinned);

const buttonLabel = computed(
  () =>
    `${$t('ibe.form.label.phone-country', 'Country code')}: ${selectedCountry.value.name} (+${selectedCountry.value.callingCode})`
);

function emitValue() {
  lastEmitted = joinPhone(country.value, national.value);
  emit('update:modelValue', lastEmitted);
}

watch(
  () => props.modelValue,
  (value) => {
    if ((value ?? '') === (lastEmitted ?? '')) return;
    const parts = splitPhone(value, country.value);
    country.value = parts.country;
    national.value = parts.national;
    lastEmitted = value ?? '';
  },
  { immediate: true }
);

function handleInput(event: Event) {
  const target = event.target as HTMLInputElement;
  const parts = parsePhoneInput(target.value, country.value);
  country.value = parts.country;
  national.value = parts.national;
  if (target.value !== parts.national) {
    target.value = parts.national;
  }
  emitValue();
}

function handleBlur(event: FocusEvent) {
  national.value = formatNational(country.value, national.value);
  emit('blur', event);
}

function handleCountryChange(code: CountryCode) {
  country.value = code;
  emitValue();
  setTimeout(() => inputRef.value?.focus());
}

const triggerRef = ref<HTMLElement | null>(null);
const menuStyle = ref<Record<string, string>>({});

/** Same fixed positioning / flip-up logic as BaseDropdown, spanning the whole field. */
function updatePosition() {
  const el = triggerRef.value;
  if (!el) return;
  const rect = el.getBoundingClientRect();
  const gap = 4;
  const menuMaxHeight = 240 + 24;
  const spaceBelow = window.innerHeight - rect.bottom;
  const flipUp = spaceBelow < menuMaxHeight && rect.top > spaceBelow;
  menuStyle.value = flipUp
    ? {
        position: 'fixed',
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        bottom: `${window.innerHeight - rect.top + gap}px`,
      }
    : {
        position: 'fixed',
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        top: `${rect.bottom + gap}px`,
      };
}

function addReposListeners() {
  window.addEventListener('scroll', updatePosition, true);
  window.addEventListener('resize', updatePosition);
}

function removeReposListeners() {
  window.removeEventListener('scroll', updatePosition, true);
  window.removeEventListener('resize', updatePosition);
}

function handleMenuEnter() {
  updatePosition();
  nextTick(updatePosition);
  addReposListeners();
}

onUnmounted(removeReposListeners);

const optionClasses = (active: boolean, selected: boolean) => [
  'flex items-center gap-2.5 px-5 py-2 cursor-pointer transition-colors duration-150 dropdown-option',
  active || selected ? 'bg-gray-100 text-black' : 'text-grey hover:bg-gray-50',
];
</script>

<template>
  <div class="control-wrapper">
    <label v-if="label" :for="inputId" class="block mb-1 text-sm font-medium text-grey">
      {{ label }}
    </label>

    <Listbox
      v-slot="{ open }"
      :model-value="country"
      :disabled="disabled"
      @update:model-value="handleCountryChange"
    >
      <div ref="triggerRef" class="relative flex w-full">
        <ListboxButton
          :class="[
            'flex items-center gap-1.5 shrink-0 px-3 py-3 transition-colors duration-200 phone-country-button',
            error
              ? 'border-2 bg-highlight-2 border-error'
              : 'bg-white border-b-2 border-b-text-primary hover:bg-actions-tertiary',
          ]"
          :aria-label="buttonLabel"
        >
          <span
            v-if="selectedCountry.pinned"
            :class="['phone-flag', `phone-flag--${selectedCountry.code.toLowerCase()}`]"
            aria-hidden="true"
          ></span>
          <span class="text-black">+{{ selectedCountry.callingCode }}</span>
          <svg
            :class="[
              'w-4 h-4 transition-transform duration-200',
              error ? 'text-error' : 'text-black',
              open ? 'rotate-180' : '',
            ]"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </ListboxButton>

        <input
          :id="inputId"
          ref="inputRef"
          :name="name"
          type="tel"
          inputmode="tel"
          autocomplete="tel-national"
          :value="national"
          :placeholder="placeholder"
          :disabled="disabled"
          :class="[error ? 'error-text-input' : 'text-input', 'phone-national-input w-full flex-1']"
          :aria-invalid="error ? 'true' : undefined"
          :aria-describedby="error && errorMessage ? errorId : undefined"
          @input="handleInput"
          @blur="handleBlur"
        />

        <Teleport to="body">
          <transition
            leave-active-class="transition duration-100 ease-in"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
            @before-enter="handleMenuEnter"
            @after-leave="removeReposListeners"
          >
            <ListboxOptions
              class="z-50 py-3 overflow-auto bg-white shadow-md max-h-60 dropdown-menu"
              :style="menuStyle"
            >
              <ListboxOption
                v-for="option in pinnedOptions"
                :key="option.code"
                v-slot="{ active, selected }"
                :value="option.code"
                as="template"
              >
                <li :class="optionClasses(active, selected)">
                  <span
                    :class="['phone-flag', `phone-flag--${option.code.toLowerCase()}`]"
                    aria-hidden="true"
                  ></span>
                  {{ option.name }} (+{{ option.callingCode }})
                </li>
              </ListboxOption>

              <li role="separator" class="phone-separator"></li>

              <ListboxOption
                v-for="option in otherOptions"
                :key="option.code"
                v-slot="{ active, selected }"
                :value="option.code"
                as="template"
              >
                <li :class="optionClasses(active, selected)">
                  {{ option.name }} (+{{ option.callingCode }})
                </li>
              </ListboxOption>
            </ListboxOptions>
          </transition>
        </Teleport>
      </div>
    </Listbox>

    <div
      v-if="error && errorMessage"
      :id="errorId"
      class="bg-error p-2.5 pr-12 max-w-80 flex mt-2 gap-2.5 items-center text-white text-sm"
    >
      <i class="w-6 h-5 bg-white icon icon-warning shrink-0"></i>
      {{ errorMessage }}
    </div>
  </div>
</template>

<style scoped>
.phone-country-button:focus {
  outline: 2px solid #6c6b3c;
  outline-offset: -2px;
}

.dropdown-menu:focus {
  outline: 2px solid #6c6b3c;
  outline-offset: 0;
}

.phone-national-input {
  min-width: 0;
}

.phone-separator {
  margin: 0.5rem 1.25rem;
  border-top: 1px solid rgb(0 0 0 / 0.15);
}

.phone-flag {
  display: inline-block;
  width: 1.25rem;
  height: 1.25rem;
  flex-shrink: 0;
  border-radius: 9999px;
  box-shadow: inset 0 0 0 1px rgb(0 0 0 / 0.08);
}

.phone-flag--de {
  background: linear-gradient(#000 0 33.33%, #dd0000 33.33% 66.66%, #ffce00 66.66%);
}

.phone-flag--at {
  background: linear-gradient(#c8102e 0 33.33%, #fff 33.33% 66.66%, #c8102e 66.66%);
}

.phone-flag--ch {
  background:
    linear-gradient(#fff, #fff) center / 56% 18% no-repeat,
    linear-gradient(#fff, #fff) center / 18% 56% no-repeat,
    #da291c;
}
</style>
